import '@app/App.css';
import { Badge } from '@comps/ui/badge';
import { Button } from '@comps/ui/button';
import CompozzSpinner from '@comps/ui/CompozzSpinner';
import { useConfirmationDialog } from '@comps/ui/confirmation-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@comps/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from '@comps/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@comps/ui/popover';
import { Switch } from '@comps/ui/switch';
import { CompozzError, isCompozzError } from '@dto/CompozzError';
import User, { NewUserRequest, UpdateUserRequest } from '@dto/Users';
import { deleteUserRequest } from './deleteUser';
import { zodResolver } from "@hookform/resolvers/zod";
import { applyBrightness, DEFAULT_COMPOZZ_COLOR, getLightColor, GRAY_300_COLOR, IsDarkColor } from '@utils/colors';
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from '@utils/constants';
import { toastError } from '@utils/toasts';
import { getWsButtonStyle, TW_AddUserButton, TW_AddUserFormButton, TW_AddUserFormButtons, TW_AddUserFormContent, TW_AddUserFormError, TW_BackHomeButton, TW_HomeUsers, TW_HomeUsersSearch, TW_HomeUsersStatus, TW_HomeUsersStatusActive, TW_HomeUsersStatusAll, TW_HomeUsersTitle, TW_HomeUsersTitleContainer, TW_OwnerBadge, TW_UsersActionsPopoverContent, TW_UsersActionsPopoverContentItem, TW_UsersActiveCell, TW_UsersBodyRowActions, TW_UsersBodyRows, TW_UsersNameCell, TW_UsersNameCellFirstLastNames, TW_UsersNameCellUsername, TW_UsersTable, useHover } from '@utils/twClasses';
import { post, preventDefault, put } from '@utils/utils';
import { ArrowLeft } from 'iconsax-react';
import { Ellipsis } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { translate } from 'src/i18n/i18n';
import { useAppStore, useUserStore } from 'src/store';
import { z } from "zod";


function UsersSection() {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const tenant = useAppStore(state => state.tenant);
  const defaultColor = useMemo(() => tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR, [tenant?.customColor]);

  // Use default color for users page instead of workspace color
  const defaultLightColor = useMemo(() => getLightColor(defaultColor), [defaultColor]);
  const defaultDarkColor = useMemo(() => applyBrightness(defaultColor, -25), [defaultColor]);
  const defaultIsDarkColor = useMemo(() => IsDarkColor(defaultColor), [defaultColor]);

  const backHomeButtonStyle = useHover(getWsButtonStyle(defaultLightColor, defaultDarkColor), getWsButtonStyle(defaultColor, defaultDarkColor));
  const addUserButtonStyle = useHover(getWsButtonStyle(defaultColor), getWsButtonStyle(defaultDarkColor));

  
  const users = useAppStore(state => state.usersByKeys);
  const updateUsers = useAppStore(state => state.updateUsers);
  const user = useUserStore(state => state.user);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loadEnded, setLoadEnded] = useState(false);
  const LOAD_DELAY = 500;

  useEffect(() => {
    setAllUsers(Array.from(users.values()));
  }, [users]);

  useEffect(() => {
    setTimeout(() => {
      setLoadEnded(true);
    }, LOAD_DELAY)
  }, [])

  function NameCell(user: User) {
    return <div className={TW_UsersNameCell}>
      <span className={TW_UsersNameCellFirstLastNames}>{user.firstname} {user.lastname}</span>
      <span className={TW_UsersNameCellUsername}>{user.username}</span>
    </div>;
  }

  function OwnerCell(user: User) {
    if (!user.owner) {
      return null;
    }
    return <Badge className={TW_OwnerBadge}
      style={{
        backgroundColor: defaultColor,
        color: defaultIsDarkColor ? 'white' : 'black',
      }}>{t('users.table.body.owner')}</Badge>;
  }

  function getChangeActiveFct(user: User) {
    const res = function changeActive(active: boolean) {

      const updateUserRequest: UpdateUserRequest = {
        userKey: user._key,
        firstname: user.firstname,
        lastname: user.lastname,
        order: user.order,
        active: !user.active,
        owner: user.owner,
        rights: undefined
      };
      put<User>(`${BASE_URL}/admin/users`, updateUserRequest).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toastError(translate(t, cError.i18n, cError.params));
            } else {
              toastError(cError.message);
            }
            const newUsers = new Map(users);
            newUsers.set(user._key, user);
            updateUsers(newUsers);
          } else {
            const newUsers = new Map(users);
            newUsers.set(user._key, response as User);
            updateUsers(newUsers);
          }
        }
      });
    }
    return res;
  }



  function ActiveCell(user: User) {
    return <Switch className={TW_UsersActiveCell} checked={user.active || false} onCheckedChange={getChangeActiveFct(user)} style={{ backgroundColor: (user.active ? defaultColor : GRAY_300_COLOR) }} />
  }

  function AddUserForm() {
    const orgId = useUserStore(state => state.orgId);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);  
    const cancelButtonStyle = useHover(getWsButtonStyle(defaultColor), getWsButtonStyle(defaultDarkColor));
    const okButtonStyle = useHover(getWsButtonStyle(defaultColor), getWsButtonStyle(defaultDarkColor));

    const formSchema = z.object({
      firstname: z.string().min(1, {
        message: t("users.add.form.error.firstname"),
      }),
      lastname: z.string().min(1, {
        message: t("users.add.form.error.lastname"),
      }),
      email: z.string().email({
        message: t("users.add.form.error.email"),
      }),
      username: z.string().min(1, {
        message: t("users.add.form.error.username"),
      }).regex(/^[a-z0-9\-.]+(@[a-z0-9\-.]+)?$/, {
        message: t("users.add.form.error.username.format"),
      }),
      sendVerifEmail: z.boolean().default(true),
    })

    const form = useForm<z.infer<typeof formSchema>>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        firstname: "",
        lastname: "",
        email: "",
        username: "",
        sendVerifEmail: true,
      },
    })

    const addUser = function addUser() {
      if (!orgId) {
        setError(t("users.add.form.error.orgId"));
        return;
      }
      setLoading(true);
      const newUserRequest: NewUserRequest = {
        firstname: form.getValues("firstname"),
        lastname: form.getValues("lastname"),
        email: form.getValues("email"),
        username: form.getValues("username"),
        tenant: orgId,
        sendVerifEmail: form.getValues("sendVerifEmail"),
        order: (allUsers.length > 0) ? allUsers[allUsers.length - 1].order + DEFAULT_ORDER_INCREMENT : DEFAULT_ORDER_INCREMENT
      };
      post<User>(`${BASE_URL}/admin/users`, newUserRequest).then(response => {

        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              setError(translate(t, cError.i18n, cError.params));
            } else {
              setError(cError.message);
            }
          } else {
            const newUsers = new Map(users);
            newUsers.set(response._key, response as User);
            updateUsers(newUsers);
            setAddUserDialogOpen(false);
            setError("");
          }
        }
        setLoading(false);
      });
    }

    function onInputKeyDown(event: React.KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit(addUser)();
      }
    }

    function onEmailInputBlur(event: React.FocusEvent<HTMLInputElement>) {
      const email = form.getValues("email");
      const username = form.getValues("username");
      if (email && !username) {
        form.setValue("username", email);
      }
    }

    if (loading) {
      return (
        <div className="flex flex-col justify-center items-center h-full gap-4 mt-6 m-auto">
          <CompozzSpinner />
        </div>
      )
    }
    return (
      <Form {...form}>
        <form onSubmit={preventDefault} className="space-y-8 w-full mt-6">
          <FormField
            control={form.control}
            name="firstname"
            render={({ field }) => (
              <FormItem aria-required>
                <FormLabel>{t("users.add.form.firstname")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("users.add.form.firstname.placeholder")} {...field} required onKeyDown={onInputKeyDown} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastname"
            render={({ field }) => (
              <FormItem aria-required>
                <FormLabel>{t("users.add.form.lastname")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("users.add.form.lastname.placeholder")} {...field} required onKeyDown={onInputKeyDown} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem aria-required >
                <FormLabel>{t("users.add.form.email")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("users.add.form.email.placeholder")} {...field} required onKeyDown={onInputKeyDown} onBlur={onEmailInputBlur} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem aria-required >
                <FormLabel>{t("users.add.form.username")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("users.add.form.username.placeholder")} {...field} required onKeyDown={onInputKeyDown} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sendVerifEmail"
            render={({ field }) => (
              <FormItem className="flex flex-row justify-start items-center gap-4">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} style={{ backgroundColor: (field.value ? defaultColor : GRAY_300_COLOR) }} />
                </FormControl>
                <FormLabel style={{ marginTop: 0 }}>{t("users.add.form.sendVerifEmail")}</FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className={TW_AddUserFormError}>{error}</div>
          <div className={TW_AddUserFormButtons}>
            <Button className={TW_AddUserFormButton} {...cancelButtonStyle} onClick={() => setAddUserDialogOpen(false)}>{t("general.button.cancel.text")}</Button>
            <Button className={TW_AddUserFormButton} {...okButtonStyle} onClick={form.handleSubmit(addUser)} >{t("users.add.form.add")}</Button>
          </div>
        </form>
      </Form>
    )
  }


  function filterUsers(usersToFilter: User[]) {
    return usersToFilter.filter(user =>
      user.firstname?.toLowerCase()?.includes(search.toLowerCase()) ||
      user.lastname?.toLowerCase()?.includes(search.toLowerCase()) ||
      user.username?.toLowerCase()?.includes(search.toLowerCase())
    );
  }

  const activeUsers = allUsers.filter(user => user.active).length;
  const filteredUsers = filterUsers(allUsers);
  return (
    <section className={TW_HomeUsers}>
      <button className={TW_BackHomeButton}
        onClick={() => navigate("/")}
        autoFocus={false}
        {...backHomeButtonStyle}
      >
        <ArrowLeft />{t('home.users.button.back.home')}
      </button>
      {user && user.owner &&
        <>
          <div className={TW_HomeUsersTitleContainer}>
            <h1 className={TW_HomeUsersTitle}>{t('home.users.title')}</h1>
            <div className={TW_HomeUsersStatus}>
              <span className={TW_HomeUsersStatusActive}>{translate(t, 'home.users.user.status.active', [activeUsers])}</span>
              <span className={TW_HomeUsersStatusAll}>{translate(t, 'home.users.user.status.all', [tenant?.maxUsers || 0])}</span>
            </div>
            <input type="text" placeholder={t('home.users.search')} className={TW_HomeUsersSearch} value={search} onChange={(e) => setSearch(e.target.value)} />
            <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
              <DialogTrigger><p className={TW_AddUserButton} {...addUserButtonStyle}>{t('home.users.add.button')}</p></DialogTrigger>
              <DialogContent className={TW_AddUserFormContent}>
                <DialogHeader>
                  <DialogTitle>{t("users.add.form.title")}</DialogTitle>
                </DialogHeader>
                <AddUserForm />
              </DialogContent>
            </Dialog>
          </div>
          <table className={TW_UsersTable}>
            <thead>
              <tr>
                <th className="w-1/2">{t('users.table.header.name')}</th>
                <th className="hidden md:table-cell">{t('users.table.header.owner')}</th>
                <th className=" align-center text-gray-400 text-sm text-normal">{t('users.table.header.status')}</th>
                <th className="">{t('users.table.header.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                return (
                  <tr key={user._key} className={TW_UsersBodyRows}>
                    <td>{NameCell(user)}</td>
                    <td className="hidden md:table-cell text-center">{OwnerCell(user)}</td>
                    <td className="text-center">{ActiveCell(user)}</td>
                    <td className="text-center">
                      <UserActionsButton user={user} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      }
      {(!user || !user.owner) && <p className={"mt-4 transition duration-300 ease-in-out " + (loadEnded ? "opacity-100" : "opacity-0")}>You do not have the rights to access to users administration.</p>}
    </section>
  );
};

function UserActionsButton({ user }: { user: User }) {
  const users = useAppStore(state => state.usersByKeys);
  const updateUsers = useAppStore(state => state.updateUsers);
  const [changeInfosDialogOpen, setChangeInfosDialogOpen] = useState(false);
  const [userActionsOpen, setUserActionsOpen] = useState(false);
  const { t } = useTranslation();
  const tenant = useAppStore(state => state.tenant);
  const confirmDialog = useConfirmationDialog();
  const defaultColor = useMemo(() => tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR, [tenant?.customColor]);
  const defaultDarkColor = useMemo(() => applyBrightness(defaultColor, -25), [defaultColor]);

  function getChangeOwnerFct(user: User, owner: boolean) {
    const res = function changeOwner() {

      const updateUserRequest: UpdateUserRequest = {
        userKey: user._key,
        firstname: user.firstname,
        lastname: user.lastname,
        order: user.order,
        active: user.active,
        owner: owner,
        rights: undefined
      };
      put<User>(`${BASE_URL}/admin/users`, updateUserRequest).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toast.error(translate(t, cError.i18n, cError.params));
            } else {
              toast.error(cError.message);
            }
            const newUsers = new Map(users);
            newUsers.set(user._key, user);
            updateUsers(newUsers);
          } else {
            const newUsers = new Map(users);
            newUsers.set(user._key, response as User);
            updateUsers(newUsers);
          }
        }
        setUserActionsOpen(false);
      });
    }
    return res;
  }

  function sendVerifEmailFct(user: User) {
    const res = function sendVerifEmail() {
      if (!tenant) {
        console.error(t("users.verifyEmail.error.tenantNotFound"));
        return;
      }
      setUserActionsOpen(false);
      const request = {
        username: user.username,
        tenant: tenant._key
      };
      post<void>(`${BASE_URL}/admin/users/verifyEmail`, request).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toast.error(translate(t, cError.i18n, cError.params));
            } else {
              toast.error(cError.message);
            }
          }
        } else {
          toast.success(t("users.verifyEmail.success"));
        }
      });
    }
    return res;
  }

  function resetPasswordFct(user: User) {
    const res = function resetPassword() {
      if (!tenant) {
        console.error(t("users.resetPassword.error.tenantNotFound"));
        return;
      }
      setUserActionsOpen(false);
      const request = {
        username: user.username,
        tenant: tenant._key
      };
      post<void>(`${BASE_URL}/admin/users/resetPassword`, request).then(response => {
        if (response) {
          if (isCompozzError(response)) {
            const cError = response as CompozzError;
            if (cError.i18n) {
              toast.error(translate(t, cError.i18n, cError.params));
            } else {
              toast.error(cError.message);
            }
          }
        } else {
          toast.success(t("users.resetPassword.success"));
        }
      });
    }
    return res;
  }

  function deleteUserFct(user: User) {
    const res = async function deleteUser() {
      if (!tenant) {
        console.error(t("users.delete.error.tenantNotFound"));
        return;
      }
      setUserActionsOpen(false);
      const confirmed = await confirmDialog({
        title: t("users.delete.confirmation.title"),
        description: translate(t, "users.delete.confirmation.description", [user.username]),
        cancelButtonStyle: getWsButtonStyle(defaultColor),
        cancelButtonHoverStyle: getWsButtonStyle(defaultDarkColor),
        confirmButtonStyle: getWsButtonStyle(defaultColor),
        confirmButtonHoverStyle: getWsButtonStyle(defaultDarkColor),
      });
      if (!confirmed) {
        return;
      }
      await deleteUserRequest({ tenantKey: tenant._key, user, users, updateUsers, t });
    }
    return res;
  }

  const onChangeInfosCancelled = function () {
    setChangeInfosDialogOpen(false);
    setUserActionsOpen(false);
  }

  const onChangeInfosOpenClose = function (open: boolean) {
    setChangeInfosDialogOpen(open);
    if (!open) {
      setUserActionsOpen(false);
    }
  }

  function ChangeInfosForm({ user }: { user: User }) {
    const [error, setError] = useState("");
    const cancelButtonStyle = useHover(getWsButtonStyle(defaultColor), getWsButtonStyle(defaultDarkColor));
    const okButtonStyle = useHover(getWsButtonStyle(defaultColor), getWsButtonStyle(defaultDarkColor));

    const formSchema = z.object({
      firstname: z.string().min(1, {
        message: t("users.modify.form.error.firstname"),
      }),
      lastname: z.string().min(1, {
        message: t("users.modify.form.error.lastname"),
      }),
    })

    const form = useForm<z.infer<typeof formSchema>>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        firstname: user.firstname,
        lastname: user.lastname,
      },
    })

    function getChangeInfos(user: User, firstname: string, lastname: string) {
      const res = function changeInfos() {

        const updateUserRequest: UpdateUserRequest = {
          userKey: user._key,
          firstname: firstname,
          lastname: lastname,
          order: user.order,
          active: user.active,
          owner: user.owner,
          rights: undefined
        };
        put<User>(`${BASE_URL}/admin/users`, updateUserRequest).then(response => {
          if (response) {
            if (isCompozzError(response)) {
              const cError = response as CompozzError;
              if (cError.i18n) {
                setError(translate(t, cError.i18n, cError.params));
              } else {
                setError(cError.message);
              }
              const newUsers = new Map(users);
              newUsers.set(user._key, user);
              updateUsers(newUsers);
            } else {
              const newUsers = new Map(users);
              newUsers.set(user._key, response as User);
              updateUsers(newUsers);
              setChangeInfosDialogOpen(false);
              setUserActionsOpen(false);
            }
          }
        });
      }
      return res;
    }

    const onInputEnter = function (event: React.KeyboardEvent) {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        getChangeInfos(user, form.getValues("firstname"), form.getValues("lastname"))();
      }
    }

    return (
      <Form {...form}>
        <form onSubmit={preventDefault} className="space-y-8 w-full mt-6">
          <FormField
            control={form.control}
            name="firstname"
            render={({ field }) => (
              <FormItem aria-required onKeyDown={onInputEnter}>
                <FormLabel>{t("users.modify.form.firstname")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("users.modify.form.firstname.placeholder")} {...field} required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastname"
            render={({ field }) => (
              <FormItem aria-required onKeyDown={onInputEnter}>
                <FormLabel>{t("users.modify.form.lastname")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("users.modify.form.lastname.placeholder")} {...field} required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className={TW_AddUserFormButtons}>
            <div className={TW_AddUserFormError}>{error}</div>
            <Button className={TW_AddUserFormButton} {...cancelButtonStyle} onClick={onChangeInfosCancelled}>{t("general.button.cancel.text")}</Button>
            <Button className={TW_AddUserFormButton} {...okButtonStyle} onClick={() => form.handleSubmit(getChangeInfos(user, form.getValues("firstname"), form.getValues("lastname")))()}>{t("users.modify.form.validate")}</Button>
          </div>
        </form>
      </Form>
    )
  }

  return (
    <Popover open={userActionsOpen} onOpenChange={setUserActionsOpen}>
      <PopoverTrigger className="bg-transparent">
        <Ellipsis className={TW_UsersBodyRowActions} />
      </PopoverTrigger>
      <PopoverContent className={TW_UsersActionsPopoverContent}>
        <Dialog open={changeInfosDialogOpen} onOpenChange={onChangeInfosOpenClose} >
          <DialogTrigger>
            <p className={TW_UsersActionsPopoverContentItem}>{t('users.table.body.actions.changeInfos')}</p>
          </DialogTrigger>
          <DialogContent className={TW_AddUserFormContent}>
            <DialogHeader>
              <DialogTitle>{t("users.modify.form.title")}</DialogTitle>
            </DialogHeader>
            <ChangeInfosForm user={user} />
          </DialogContent>
        </Dialog>
        <p className={TW_UsersActionsPopoverContentItem} onClick={getChangeOwnerFct(user, !user.owner)}>{(user.owner ? t('users.table.body.actions.unsetOwner') : t('users.table.body.actions.setOwner'))}</p>
        <p className={TW_UsersActionsPopoverContentItem} onClick={sendVerifEmailFct(user)}>{t('users.table.body.actions.sendVerifEmail')}</p>
        <p className={TW_UsersActionsPopoverContentItem} onClick={resetPasswordFct(user)}>{t('users.table.body.actions.resetPassword')}</p>
        <p className={TW_UsersActionsPopoverContentItem} onClick={deleteUserFct(user)}>{t('users.table.body.actions.deleteUser')}</p>
      </PopoverContent>
    </Popover>
  )
}

export default UsersSection;